import { useMemo, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { ChevronDown, ChevronUp } from "../../common/icons";
import { Calendar, ClipboardList, MessageSquare, Notebook, ShieldCheck, ShieldOff, SquareUserRound, Users } from "lucide-react";
import ClientMatchesModal from "../modals/ClientMatchesModal";
import NotesModal from "../modals/NotesModal";
import AspApprovedModal from "../modals/AspApprovedModal";
import BlockedRelationshipsModal from "../modals/BlockedRelationshipsModal";
import StaffInProcessStaffingModal from "../modals/StaffInProcessStaffingModal";
import { openStaffingGroupByReferenceId } from "@/utils/staffingUtil";

export interface IStaffContextMenuProps {
    staff: string;
    staffName: string | null;
    staffRole?: string | null;
    isPosting: boolean;
    isMatch: boolean;
    clientId?: number;
    processId?: number;
}

interface IStaffContextMenuOption {
    icon: JSX.Element;
    title: string;
    func: () => void;
}

const StaffContextMenu = ({ staff, staffName, staffRole, isPosting, isMatch, clientId, processId }: IStaffContextMenuProps) => {
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [matchesModalOpen, setMatchesModalOpen] = useState<boolean>(false);
    const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);
    const [aspModalOpen, setAspModalOpen] = useState<boolean>(false);
    const [blockedRelationshipsOpen, setBlockedRelationshipsOpen] = useState<boolean>(false);
    const [inProcessOpen, setInProcessOpen] = useState<boolean>(false);
    const staffLoginName = staff.includes('\\') ? staff.split('\\')[1] : staff;

    const menuOptions: IStaffContextMenuOption[] = useMemo<IStaffContextMenuOption[]>(() => {
        const openUserForm = () => {
            window.open('https://sharepoint.teampbs.com/SitePages/StaffForm.aspx?isdlg=1&username=' + staff + '#/', "_blank", "noopener,noreferrer");
        };
        const openSchedule = () => {
            window.open('https://sharepoint.teampbs.com/SiteAssets/ScheduleProjection/index.aspx#/schedule-status?login='+ staff + '&mode=draft', "_blank", "noopener,noreferrer")
        }
        const options: IStaffContextMenuOption[] = [];
        options.push({
            icon: <SquareUserRound className="h-4 w-4" />,
            title: 'Staff File',
            func: () => openUserForm()
        })
        options.push({
            icon: <Calendar className="h-4 w-4" />,
            title: 'Draft Schedule',
            func: () => openSchedule()
        })
        options.push({
            icon: <Notebook className="h-4 w-4" />,
            title: 'Notes',
            func: () => setNotesModalOpen(true)
        })
        options.push({
            icon: <ClipboardList className="h-4 w-4" />,
            title: 'Staffing in Process',
            func: () => setInProcessOpen(true)
        })
        if (!isPosting) {
            options.push({
                icon: <ShieldOff className="h-4 w-4" />,
                title: 'Blocked Relationships',
                func: () => setBlockedRelationshipsOpen(true)
            });
        }
        if (!isMatch) {
            options.push({
                icon: <Users className="h-4 w-4" />,
                title: 'Matched Clients',
                func: () => setMatchesModalOpen(true)
            })
        }
        if (isPosting && clientId !== undefined) {
            options.push({
                icon: <ShieldCheck className="h-4 w-4" />,
                title: 'ASPApproved',
                func: () => setAspModalOpen(true)
            })
        }
        if (isPosting && clientId !== undefined && processId !== undefined) {
            options.push({
                icon: <MessageSquare className="h-4 w-4" />,
                title: 'Staffing Chat',
                func: () => openStaffingGroupByReferenceId(`${processId}-${staffLoginName}-${clientId}`)
            })
        }

        return options;
    }, [isMatch, isPosting, clientId, processId, staff, staffLoginName]);
    return (
        <>
            <DropdownMenu onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="min-h-[48px] px-3 rounded">
                        {staffName}
                        {menuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48">
                    {menuOptions.map(({ icon, title, func }) => (
                        <DropdownMenuItem key={title} onClick={func}>
                            {icon}
                            {title}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <ClientMatchesModal
                open={matchesModalOpen}
                onOpenChange={setMatchesModalOpen}
                identifier={staff}
                name={staffName}
                role={staffRole}
                isApplicant={false}
            />
            <NotesModal
                open={notesModalOpen}
                onOpenChange={setNotesModalOpen}
                identifier={staff}
                userType={'staff'}
                displayName={staffName ?? ''}
                onCountChange={() => { }}
            />
            {isPosting && clientId !== undefined && (
                <AspApprovedModal
                    open={aspModalOpen}
                    onOpenChange={setAspModalOpen}
                    staffLoginName={staffLoginName}
                    staffName={staffName}
                    clientId={clientId}
                    processId={processId}
                />
            )}
            <BlockedRelationshipsModal
                open={blockedRelationshipsOpen}
                onOpenChange={setBlockedRelationshipsOpen}
                staff={staff}
                staffName={staffName}
            />
            <StaffInProcessStaffingModal
                open={inProcessOpen}
                onOpenChange={setInProcessOpen}
                staff={staff}
                staffName={staffName}
            />
        </>
    );
};

export default StaffContextMenu;
